import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

type DeleteProductLineArgs = {
    id: number;
    /**
     * When true, sends `?force=true` so the backend cascades:
     * detaches every product attached to the target line or any of its
     * descendants (sets product.product_line_id = NULL), then drops the
     * whole subtree (root + descendants). Used by the "delete anyway" path
     * of the impact-confirmation modal.
     */
    force?: boolean;
};

export function useDeleteProductLine() {
    const queryClient = useQueryClient();
    const query = useMutation({
        mutationFn: async ({ id, force = false }: DeleteProductLineArgs) => {
            const url = force ? `/api/product_lines/${id}?force=true` : `/api/product_lines/${id}`;
            const response = await loggedInApiCall.delete(url);
            return response;
        },
        onSuccess: () => queryClient.invalidateQueries(["allProductLines"]),
    });

    return query;
}

export type ProductLineDeleteImpact = {
    descendantsCount: number;
    directProductsCount: number;
    totalProductsCount: number;
};

/**
 * Parses the structured 409 message emitted by ProductLineProcessor:
 *   `Product line "X" has N sub-line(s), M direct product(s) and T total product(s) in its hierarchy.`
 *
 * Each number is captured independently so the modal can render the impact
 * summary even if the exact wording shifts. Missing matches fall back to 0
 * so the UI degrades gracefully rather than lying about the impact.
 */
export const extractProductLineDeleteImpact = (message: unknown): ProductLineDeleteImpact => {
    if (typeof message !== "string") {
        return { descendantsCount: 0, directProductsCount: 0, totalProductsCount: 0 };
    }

    const subLineMatch = message.match(/(\d+)\s+sub-line/i);
    const directMatch = message.match(/(\d+)\s+direct\s+product/i);
    const totalMatch = message.match(/(\d+)\s+total\s+product/i);

    return {
        descendantsCount: subLineMatch ? parseInt(subLineMatch[1], 10) : 0,
        directProductsCount: directMatch ? parseInt(directMatch[1], 10) : 0,
        totalProductsCount: totalMatch ? parseInt(totalMatch[1], 10) : 0,
    };
};
